import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { getMetadataForPath } from "../services/seoService";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "product";
  structuredData?: object[];
  noIndex?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title: propTitle,
  description: propDescription,
  keywords: propKeywords,
  canonical: propCanonical,
  ogImage: propOgImage,
  ogType: propOgType,
  structuredData: propStructuredData = [],
  noIndex: propNoIndex,
}) => {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const pathMeta = getMetadataForPath(path);

  // Prioritize props, then JSON config, then hardcoded defaults
  const title = propTitle || pathMeta.title;
  const description = propDescription || pathMeta.description;
  const keywords = propKeywords || pathMeta.keywords;
  const canonical = propCanonical || pathMeta.canonical;
  const ogImage = propOgImage || pathMeta.ogImage;
  const ogType = propOgType || pathMeta.ogType;
  const noIndex = propNoIndex !== undefined ? propNoIndex : pathMeta.noIndex;

  // Combine all sources of structured data
  const jsonStructuredData = pathMeta.structuredData || [];
  const combinedStructuredData = [...jsonStructuredData, ...propStructuredData];
  const baseUrl = "https://moizwp.com";
  const isReactSnap =
    typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";
  const rawUrl =
    canonical ||
    (typeof window !== "undefined" ? window.location.href : baseUrl);
  const currentUrl = (
    isReactSnap ? rawUrl.replace(/http:\/\/localhost:\d+/, baseUrl) : rawUrl
  ).replace(/\/$/, "");

  // Default structured data
  const defaultStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description: description,
      url: currentUrl,
      mainEntity: {
        "@type": "Organization",
        name: "MoizWP",
        url: baseUrl,
        logo: `${baseUrl}/logo.png`,
        sameAs: [
          "https://www.facebook.com/MoizWP",
          "https://x.com/MoiWordpress",
          "https://www.linkedin.com/in/imran-javed-full-stack-developer/",
          "https://www.youtube.com/@MoizWP",
        ],
        description: "Professional web design and development agency",
      },
    },
  ];

  // Merge default and custom structured data
  const allStructuredData = [
    ...defaultStructuredData,
    ...combinedStructuredData,
  ];

  // Track page view for analytics
  useEffect(() => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", "G-XLMPWHRHEB", {
        page_title: title,
        page_location: currentUrl,
        page_description: description,
      });
    }
  }, [title, currentUrl, description]);

  const absoluteOgImage = ogImage.startsWith("http")
    ? ogImage
    : `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:site_name" content="MoizWP" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={absoluteOgImage} />

      {/* Structured Data */}
      {allStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
